/**
 * Linear RGB interpolation along numeric stops (e.g. rate 0–1 → color ramp).
 * Shared by stream-quality minimap/KPIs and generic progress indicators.
 */

export type GradientColorStop = {
  /** Scalar position along the ramp, typically in [0, 1]. */
  rate: number;
  /** CSS color (`#rgb`, `#rrggbb`, `rgb()`, `rgba()`). */
  color: string;
};

type Rgb = { r: number; g: number; b: number };

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Parses common CSS color forms (`#rgb`, `#rrggbb`, `rgb()`, `rgba()`) for gradient interpolation.
 */
export function parseCssColorToRgb(cssColor: string): Rgb | null {
  const trimmed = cssColor.trim();
  if (trimmed.startsWith('#')) {
    const h = trimmed.slice(1);
    if (h.length === 3) {
      return {
        r: parseInt(h[0] + h[0], 16),
        g: parseInt(h[1] + h[1], 16),
        b: parseInt(h[2] + h[2], 16),
      };
    }
    if (h.length === 6) {
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
      };
    }
    return null;
  }
  const m = trimmed.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (m) {
    return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) };
  }
  return null;
}

function rgbToCss(rgb: Rgb): string {
  return `rgb(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)})`;
}

function lerpRgb(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + t * (b.r - a.r),
    g: a.g + t * (b.g - a.g),
    b: a.b + t * (b.b - a.b),
  };
}

/**
 * Linear RGB blend across sorted stops. `rate` is clamped to [0, 1], then mapped along stop `rate` values.
 */
export function interpolateGradientColor(rate: number, stops: GradientColorStop[]): string {
  if (stops.length === 0) {
    return 'rgb(128, 128, 128)';
  }
  const sorted = [...stops].sort((a, b) => a.rate - b.rate);
  const t = clamp01(rate);
  if (t <= sorted[0].rate) {
    return sorted[0].color;
  }
  if (t >= sorted[sorted.length - 1].rate) {
    return sorted[sorted.length - 1].color;
  }
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (t >= a.rate && t <= b.rate) {
      const range = b.rate - a.rate || 1;
      const localT = (t - a.rate) / range;
      const ca = parseCssColorToRgb(a.color);
      const cb = parseCssColorToRgb(b.color);
      if (!ca || !cb) {
        return localT < 0.5 ? a.color : b.color;
      }
      return rgbToCss(lerpRgb(ca, cb, localT));
    }
  }
  return sorted[sorted.length - 1].color;
}
